import { type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'
import { PROVIDER_SIGN_UP_MUTATION } from '../../graphql/auth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import FormError from '../../components/ui/FormError'
import type { CurrentUser } from '../../contexts/AuthContext'

interface ProviderSignUpData {
  providerSignUp: {
    user: CurrentUser | null
    token: string | null
    errors: string[]
  }
}

interface Fields {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  passwordConfirmation: string
}

function validate(f: Fields): Partial<Record<keyof Fields, string>> {
  const errs: Partial<Record<keyof Fields, string>> = {}
  if (!f.firstName.trim()) errs.firstName = 'Το όνομα είναι υποχρεωτικό'
  if (!f.lastName.trim()) errs.lastName = 'Το επώνυμο είναι υποχρεωτικό'
  if (!f.email) errs.email = 'Το email είναι υποχρεωτικό'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = 'Μη έγκυρο email'
  if (!f.phone.trim()) errs.phone = 'Το τηλέφωνο είναι υποχρεωτικό'
  else if (!/^[0-9+\s\-()]{7,}$/.test(f.phone)) errs.phone = 'Μη έγκυρος αριθμός τηλεφώνου'
  if (!f.password) errs.password = 'Ο κωδικός είναι υποχρεωτικός'
  else if (f.password.length < 8) errs.password = 'Τουλάχιστον 8 χαρακτήρες'
  if (!f.passwordConfirmation) errs.passwordConfirmation = 'Επιβεβαιώστε τον κωδικό'
  else if (f.password !== f.passwordConfirmation) errs.passwordConfirmation = 'Οι κωδικοί δεν ταιριάζουν'
  return errs
}

export default function ProviderSignUp() {
  const { signIn } = useAuth()
  const { t, locale } = useLang()
  const navigate = useNavigate()

  const [fields, setFields] = useState<Fields>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
  })
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Fields, string>>>({})
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({})

  const [mutate, { loading }] = useMutation<ProviderSignUpData>(PROVIDER_SIGN_UP_MUTATION)

  function handleChange(key: keyof Fields, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function handleBlur(key: keyof Fields) {
    setTouched((prev) => ({ ...prev, [key]: true }))
    const errs = validate({ ...fields })
    setFieldErrors((prev) => ({ ...prev, [key]: errs[key] ?? '' }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errs = validate(fields)
    if (Object.keys(errs).length) {
      setFieldErrors(errs)
      const allTouched: Partial<Record<keyof Fields, boolean>> = {}
      ;(Object.keys(fields) as (keyof Fields)[]).forEach((k) => (allTouched[k] = true))
      setTouched(allTouched)
      return
    }

    setServerErrors([])
    try {
      const { data } = await mutate({
        variables: {
          firstName: fields.firstName,
          lastName: fields.lastName,
          email: fields.email,
          phone: fields.phone,
          password: fields.password,
          passwordConfirmation: fields.passwordConfirmation,
          locale,
        },
      })

      const result = data?.providerSignUp
      if (!result) return

      if (result.errors.length) {
        setServerErrors(result.errors)
        return
      }

      if (result.token && result.user) {
        await signIn(result.token, result.user)
        navigate('/provider/onboarding', { replace: true })
      }
    } catch {
      setServerErrors([t('Κάτι πήγε στραβά. Δοκιμάστε ξανά.', 'Something went wrong. Please try again.')])
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary-100 text-secondary-600 mb-4">
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 3a3 3 0 110 6 3 3 0 010-6zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08A7.2 7.2 0 0112 19.2z" fill="currentColor" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {t('Εγγραφή ως επαγγελματίας', 'Register as a Provider')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('Βρείτε πελάτες και αναπτύξτε την επιχείρησή σας', 'Find clients and grow your business')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormError errors={serverErrors} />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('Όνομα', 'First Name')}
                placeholder={locale === 'el' ? 'Γιώργης' : 'Giorgis'}
                value={fields.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                error={touched.firstName ? fieldErrors.firstName : ''}
                required
                autoComplete="given-name"
              />
              <Input
                label={t('Επώνυμο', 'Last Name')}
                placeholder={locale === 'el' ? 'Παπαδόπουλος' : 'Papadopoulos'}
                value={fields.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                error={touched.lastName ? fieldErrors.lastName : ''}
                required
                autoComplete="family-name"
              />
            </div>

            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              value={fields.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              error={touched.email ? fieldErrors.email : ''}
              required
              autoComplete="email"
            />

            <Input
              type="tel"
              label={t('Κινητό τηλέφωνο', 'Phone Number')}
              placeholder="+30 69X XXX XXXX"
              value={fields.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              error={touched.phone ? fieldErrors.phone : ''}
              hint={t('Θα χρησιμοποιηθεί για επικοινωνία με πελάτες', 'Used to communicate with clients')}
              required
              autoComplete="tel"
            />

            <Input
              type="password"
              label={t('Κωδικός πρόσβασης', 'Password')}
              placeholder="••••••••"
              value={fields.password}
              onChange={(e) => handleChange('password', e.target.value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? fieldErrors.password : ''}
              hint={t('Τουλάχιστον 8 χαρακτήρες', 'At least 8 characters')}
              required
              autoComplete="new-password"
            />

            <Input
              type="password"
              label={t('Επιβεβαίωση κωδικού', 'Confirm Password')}
              placeholder="••••••••"
              value={fields.passwordConfirmation}
              onChange={(e) => handleChange('passwordConfirmation', e.target.value)}
              onBlur={() => handleBlur('passwordConfirmation')}
              error={touched.passwordConfirmation ? fieldErrors.passwordConfirmation : ''}
              required
              autoComplete="new-password"
            />

            <Button type="submit" loading={loading} fullWidth size="lg">
              {t('Δημιουργία λογαριασμού', 'Create Account')}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              {t(
                'Με την εγγραφή σας αποδέχεστε τους Όρους Χρήσης. Ο λογαριασμός σας θα επαληθευτεί πριν την ενεργοποίησή του.',
                'By signing up you agree to our Terms of Service. Your account will be verified before activation.'
              )}
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          {t('Έχετε ήδη λογαριασμό;', 'Already have an account?')}{' '}
          <Link to="/signin" className="text-primary-600 font-medium hover:underline">
            {t('Σύνδεση', 'Sign In')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-slate-500">
          {t('Ψάχνετε επαγγελματίες;', 'Looking for cleaners?')}{' '}
          <Link to="/signup" className="text-primary-600 font-medium hover:underline">
            {t('Εγγραφή ως πελάτης', 'Register as a customer')}
          </Link>
        </p>
      </div>
    </div>
  )
}
